/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { X, Info, Plus, Minus } from "lucide-react";
import PropTypes from "prop-types";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { Badge } from "./ui/badge";
import { getColors, getFeaturedRyldoProduct } from "../services/api";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import "../styles/product-gallery.css";

const CartItem = ({ item, onUpdateQuantity, onRemove }) => (
  <div className="flex items-center py-4">
    <img
      src={item.image || "/images/products/ryldo/placeholder.jpg"}
      alt={item.name}
      className="h-20 w-20 rounded object-cover"
    />
    <div className="ml-4 flex-1">
      <h3 className="font-medium text-blue-900 dark:text-blue-100">
        {item.name}
      </h3>
      <div className="flex items-center">
        <span className="text-sm text-gray-500 dark:text-gray-400">Price:</span>
        <span className="ml-2 text-sm font-medium text-gray-900 dark:text-gray-100">
          ${item.price.toFixed(2)}
        </span>
      </div>
      <div className="mt-2 flex items-center">
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 dark:border-gray-600 dark:text-gray-200"
          onClick={() => onUpdateQuantity(item.cartItemId ?? item.id, item.quantity - 1)}
          disabled={item.quantity <= 1}
        >
          <Minus className="h-4 w-4" />
        </Button>
        <span className="mx-3 dark:text-gray-200">{item.quantity}</span>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 dark:border-gray-600 dark:text-gray-200"
          onClick={() => onUpdateQuantity(item.cartItemId ?? item.id, item.quantity + 1)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
    <Button
      variant="ghost"
      size="icon"
      className="ml-4 dark:text-gray-200 dark:hover:text-white"
      onClick={() => onRemove(item.cartItemId ?? item.id)}
    >
      <X className="h-4 w-4" />
    </Button>
  </div>
);

CartItem.propTypes = {
  item: PropTypes.shape({
    cartItemId: PropTypes.string,
    id: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
  }).isRequired,
  onUpdateQuantity: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const ProductLanding = ({ cartItems, setCartItems }) => {
  const [showNotification, setShowNotification] = useState(false);
  const [selectedColors, setSelectedColors] = useState([]);
  const [productImage, setProductImage] = useState(
    "/images/products/ryldo/placeholder.jpg",
  );
  const [productPrice, setProductPrice] = useState(69.99);
  const [availableColors, setAvailableColors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [colorCombinationImages, setColorCombinationImages] = useState({});
  const [galleryImages, setGalleryImages] = useState([]);
  const [isGalleryLoading, setIsGalleryLoading] = useState(true);
  const [stripeProduct, setStripeProduct] = useState(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [colorValidationError, setColorValidationError] = useState(false);

  // Fetch Stripe product data for The Ryldo
  useEffect(() => {
    const fetchStripeProduct = async () => {
      try {
        setIsStripeLoading(true);
        const product = await getFeaturedRyldoProduct();
        setStripeProduct(product);
        
        // Set price from Stripe if available
        if (product && product.prices && product.prices.length > 0) {
          // Get the default price or the first active price
          const defaultPrice = product.prices.find(p => p.priceId === product.defaultPriceId) || 
                               product.prices.find(p => p.active) ||
                               product.prices[0];
          
          if (defaultPrice) {
            setProductPrice(defaultPrice.unitAmount);
          }
        }
      } catch (error) {
        // Keep the hardcoded price as fallback
      } finally {
        setIsStripeLoading(false);
      }
    };

    fetchStripeProduct();
  }, []);

  // Fetch colors from the database
  useEffect(() => {
    const fetchColors = async () => {
      setIsLoading(true);

      // Define fallback combinations once, outside try/catch
      const fallbackCombinations = [
        {
          colorIds: ["BB1"],
          imageUrl: "/images/products/ryldo/ryldo-SOEF0050-BB1.jpg",
        },
        {
          colorIds: ["CH1", "CH3", "CH5"],
          imageUrl: "/images/products/ryldo/ryldo-BBSE5A-CH1-CH3-CH5.jpg",
        },
        {
          colorIds: ["EC1", "EC3", "EC5"],
          imageUrl: "/images/products/ryldo/ryldo-SOEF0050-EC1-EC3-EC5.jpg",
        },
      ];

      try {
        // Use the API service function instead of direct fetch
        const response = await getColors();

        // Check if any colors are missing the id field
        if (Array.isArray(response.colors)) {
          const missingIds = response.colors.filter((color) => !color.id);
          if (missingIds.length > 0) {
            // console.warn("Colors missing id field:", missingIds); // Optional: Log if needed during dev
          }
        }

        // Ensure we have valid colors array
        if (Array.isArray(response.colors)) {
          // Log warning for any colors missing id field
          response.colors.forEach((color) => {
            if (!color.id) {
              // console.warn("Color missing id:", color); // Optional: Log if needed during dev
            }
          });

          // Filter out any colors without an id
          const validColors = response.colors.filter((color) => color.id);
          setAvailableColors(validColors);
        }

        // Check if we have valid combinations data
        if (
          response.combinations &&
          Array.isArray(response.combinations) &&
          response.combinations.length > 0
        ) {
          // Use the combinations from the API response
          setProductCombinations(response.combinations);
        } else {
          // console.warn("No valid combinations data received, using fallbacks"); // Optional: Log if needed during dev
          setProductCombinations(fallbackCombinations);
        }

        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);

        // Fallback to some defaults in case the API fails
        setAvailableColors([
          {
            id: "EC1",
            name: "Ninja Black",
            image: "/images/colors/Pigment - Eye Candy - 1 - Ninja Black.png",
            type: "standard",
          },
          {
            id: "EC2",
            name: "Olive Yellow",
            image: "/images/colors/Pigment - Eye Candy - 2 - Olive Yellow.png",
            type: "standard",
          },
          {
            id: "EC3",
            name: "Baku Red",
            image: "/images/colors/Pigment - Eye Candy - 3 - Baku Red.png",
            type: "standard",
          },
          {
            id: "EC4",
            name: "Macaw Blue Green",
            image:
              "/images/colors/Pigment - Eye Candy - 4 - Macaw Blue Green.png",
            type: "standard",
          },
          {
            id: "EC5",
            name: "Okinawa Green",
            image: "/images/colors/Pigment - Eye Candy - 5 - Okinawa Green.png",
            type: "standard",
          },
          {
            id: "EC6",
            name: "14K Nugget Gold",
            image:
              "/images/colors/Pigment - Eye Candy - 6 - 14K Nugget Gold.png",
            type: "standard",
          },
          {
            id: "EC7",
            name: "Rainbow Blue",
            image: "/images/colors/Pigment - Eye Candy - 7 - Rainbow Blue.png",
            type: "standard",
          },
          {
            id: "EC8",
            name: "Suiren Purple",
            image: "/images/colors/Pigment - Eye Candy - 8 - Suiren Purple.png",
            type: "standard",
          },
          {
            id: "EC9",
            name: "Shide",
            image: "/images/colors/Pigment - Eye Candy - 9 - Shide.png",
            type: "standard",
          },
          {
            id: "EC10",
            name: "Okinawa Blue",
            image: "/images/colors/Pigment - Eye Candy - 10 - Okinawa Blue.png",
            type: "standard",
          },
          {
            id: "CH1",
            name: "Crystal White",
            image:
              "/images/colors/Pigment - Changtikeji - 4 - Crystal White.png",
            type: "standard",
          },
          {
            id: "CH2",
            name: "Violet",
            image: "/images/colors/Pigment - Changtikeji - 9 - Violet.png",
            type: "standard",
          },
          {
            id: "CH3",
            name: "Cobalt Blue",
            image:
              "/images/colors/Pigment - Changtikeji - 13 - Cobalt Blue.png",
            type: "standard",
          },
          {
            id: "CH4",
            name: "Bronze",
            image: "/images/colors/Pigment - Changtikeji - 15 - Bronze.png",
            type: "standard",
          },
          {
            id: "CH5",
            name: "Sunny Gold",
            image: "/images/colors/Pigment - Changtikeji - 20 - Sunny Gold.png",
            type: "standard",
          },
          {
            id: "BB1",
            name: "Light Flesh Tone",
            image: "/images/colors/Pigment - BBDINO - 1 - Light Flesh Tone.png",
            type: "skin",
          },
        ]);

        // Use the same fallback combinations defined above
        setProductCombinations(fallbackCombinations);
      }
    };

    fetchColors();
  }, []);

  // Function to set product combination images
  const setProductCombinations = (combinations) => {
    // Map the combinations to their respective images
    const combinationMap = {};

    combinations.forEach((combo) => {
      // Check if colorIds exists
      if (combo.colorIds && Array.isArray(combo.colorIds)) {
        try {
          // Create a key from color IDs - handle both string and number IDs
          const key = combo.colorIds
            .map((id) => id.toString().trim())
            .sort()
            .join(",");

          // Store the image URL with this key
          combinationMap[key] = combo.imageUrl;
        } catch (error) {
          // console.warn(
          //   `Error processing colorIds for combination: ${error.message}`,
          //   combo,
          // );
          // Generate a fallback key if there's an error
          const fallbackKey = `fallback_${Date.now()}`;
          combinationMap[fallbackKey] = combo.imageUrl;
        }
      } else {
        // If colorIds is missing or not an array, generate a fallback key
        // console.warn("Missing or invalid colorIds in combination:", combo);
        const fallbackKey = `fallback_${Date.now()}`;
        combinationMap[fallbackKey] = combo.imageUrl;
      }
    });

    // Update the color combination images
    setColorCombinationImages((prevCombinations) => ({
      ...prevCombinations,
      ...combinationMap,
    }));
  };

  // Handle color selection
  const handleColorSelect = (colorId) => {
    // Find color by id
    const color = availableColors.find((c) => c.id === colorId);

    if (!color) {
      // console.warn(
      //   `Color with ID ${colorId} not found in availableColors. Available IDs:`,
      //   availableColors.map((c) => c.id),
      // );
      return;
    }

    // Check if color is already selected
    if (selectedColors.some((c) => c.id === color.id)) {
      // console.info(`Removing color ${color.name} from selection`);
      setSelectedColors(selectedColors.filter((c) => c.id !== color.id));
      return;
    }

    // Handle skin tone selection (can only be selected alone)
    if (color.type === "skin") {
      // console.info(`Setting skin tone ${color.name} as the only selected color`);
      setSelectedColors([color]);
      return;
    }

    // If skin tone is already selected, replace it
    if (selectedColors.some((c) => c.type === "skin")) {
      // console.info(`Replacing skin tone with ${color.name}`);
      setSelectedColors([color]);
      return;
    }

    // Limit to max 3 colors
    if (selectedColors.length >= 3) {
      // console.info(
      //   `Maximum of 3 colors already selected, not adding ${color.name}`,
      // );
      return;
    }

    // console.info(`Adding color ${color.name} to selection`);
    setSelectedColors([...selectedColors, color]);
  };

  // Component to display selected colors
  const SelectedColorsDisplay = ({ selectedColors, onRemove }) => {
    if (selectedColors.length === 0) {
      return (
        <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-center text-gray-500 dark:text-gray-400 font-handwritten">
          No colors selected. Please select up to 3 colors.
        </div>
      );
    }

    // Check if a skin tone is selected
    const hasSkinTone = selectedColors.some((color) => color.type === "skin");

    return (
      <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-handwritten">
          Selected Colors:
        </h4>
        <div className="flex flex-wrap gap-2">
          {selectedColors.map((color) => (
            <div
              key={color.id}
              className="flex items-center bg-white dark:bg-gray-800 rounded-lg p-2 shadow-sm"
            >
              <div className="relative">
                <img
                  src={color.image || "/images/products/ryldo/placeholder.jpg"}
                  alt={color.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-blue-500"
                />
                <button
                  onClick={() => onRemove(color.id)}
                  className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600 focus:outline-none"
                  aria-label={`Remove ${color.name}`}
                >
                  ×
                </button>
              </div>
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 max-w-[120px]">
                {color.name.length > 15 ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help truncate block font-handwritten">
                          {color.name.substring(0, 13)}...
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs p-2">
                        {color.name}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <span className="font-handwritten">{color.name}</span>
                )}
              </span>
            </div>
          ))}
        </div>

        {/* Display notes about color combinations */}
        <div className="mt-2 space-y-2">
          {hasSkinTone && (
            <div className="text-xs text-blue-600 dark:text-blue-400 flex items-center font-handwritten">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              Skin tone can only be selected alone
            </div>
          )}

          {!colorCombinationImages[
            selectedColors
              .map((c) => c.id.toString())
              .sort()
              .join(",")
          ] && (
            <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center font-handwritten">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              No preview image available for this color combination
            </div>
          )}
        </div>
      </div>
    );
  };

  const loadGalleryImages = useCallback(async () => {
    let isMounted = true;

    if (!isMounted) return;
    setIsGalleryLoading(true);

    try {
      // If no colors are selected, show all available color combinations
      if (selectedColors.length === 0) {
        const allCombinationImages = [
          {
            original: "/images/products/ryldo/placeholder.jpg",
            thumbnail: "/images/products/ryldo/placeholder.jpg",
            originalAlt: "The Ryldo - Default View",
            thumbnailAlt: "The Ryldo - Default View Thumbnail",
          },
          ...Object.values(colorCombinationImages)
            .filter(
              (imageUrl) => imageUrl && !imageUrl.includes("placeholder.jpg"),
            )
            .map((imageUrl) => ({
              original: imageUrl,
              thumbnail: imageUrl,
              originalAlt: "The Ryldo - Available Color Configuration",
              thumbnailAlt: "The Ryldo - Color Configuration Thumbnail",
            })),
        ];

        setGalleryImages(allCombinationImages);
      } else {
        // Show selected color combination
        setGalleryImages([
          {
            original: productImage,
            thumbnail: productImage,
            originalAlt: "The Ryldo - Selected Color Configuration",
            thumbnailAlt:
              "The Ryldo - Selected Color Configuration Thumbnail",
          },
        ]);
      }
      setIsGalleryLoading(false);
    } catch (error) {
      // console.error("Error loading gallery images:", error);
      if (isMounted) {
        setGalleryImages([
          {
            original: productImage,
            thumbnail: productImage,
            originalAlt: "The Ryldo - Selected Color Configuration",
            thumbnailAlt:
              "The Ryldo - Selected Color Configuration Thumbnail",
          },
        ]);
        setIsGalleryLoading(false);
      }
    }
    return () => {
      isMounted = false;
    };
  }, [productImage, selectedColors, colorCombinationImages]);

  // Update product image based on selected colors
  useEffect(() => {
    // Default price finding logic (helper)
    const findDefaultPrice = () => {
      if (stripeProduct && stripeProduct.prices && stripeProduct.prices.length > 0) {
        return stripeProduct.prices.find(p => p.priceId === stripeProduct.defaultPriceId) || 
               stripeProduct.prices.find(p => p.active) || 
               stripeProduct.prices[0];
      }
      return null;
    };

    if (selectedColors.length > 0) {
      const sortedColorIds = selectedColors.map((c) => c.id).sort();
      const combinationKey = sortedColorIds.join("-");

      // Find matching image
      const matchingCombination = Object.entries(colorCombinationImages).find(
        ([key]) => key === combinationKey,
      );

      if (matchingCombination) {
        setProductImage(matchingCombination[1]);
      } else {
        // If no exact match, try finding a partial match (e.g., single color)
        const firstColorId = sortedColorIds[0];
        const singleColorMatch = Object.entries(colorCombinationImages).find(
          ([key]) => key === firstColorId,
        );
        if (singleColorMatch) {
          setProductImage(singleColorMatch[1]);
        } else {
          setProductImage("/images/products/ryldo/placeholder.jpg"); // Fallback
        }
      }

      // --- START: Update Price based on Number of Colors ---
      if (stripeProduct && stripeProduct.prices && stripeProduct.prices.length > 0) {
        const numSelected = selectedColors.length;
        const targetPrice = stripeProduct.prices.find(
          p => p.metadata?.num_colors === numSelected.toString()
        );

        if (targetPrice) {
          setProductPrice(targetPrice.unitAmount);
        } else {
          // Fallback to default price if specific tier not found
          const defaultPrice = findDefaultPrice();
          if (defaultPrice) {
            setProductPrice(defaultPrice.unitAmount);
          } else {
            // console.error(">>> Price NOT SET: Could not find target price tier or default price.");
          }
        }
      } else {
        // console.warn("Cannot calculate price: Stripe product or prices not loaded yet.");
      }
      // --- END: Update Price --- 

    } else {
      // Reset to default image and price if no colors selected
      setProductImage("/images/products/ryldo/placeholder.jpg");
      const defaultPrice = findDefaultPrice();
      if (defaultPrice) {
        setProductPrice(defaultPrice.unitAmount);
      } else {
        // console.warn(">>> Price NOT SET: Attempted to reset price, but no default price found.");
      }
    }
    loadGalleryImages(); // Update gallery based on selected colors potentially
  }, [selectedColors, colorCombinationImages, stripeProduct, loadGalleryImages]); // Depends on selectedColors and stripeProduct

  // Function to load gallery images based on selected colors
  

  const handleAddToCart = () => {
    // Check if at least one color is selected
    if (selectedColors.length === 0) {
      setColorValidationError(true);
      // Auto-scroll to the validation message
      setTimeout(() => {
        const addToCartButton = document.getElementById("add-to-cart-button");
        if (addToCartButton) {
          addToCartButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    // Reset validation error
    setColorValidationError(false);
    
    // Generate a unique SKU based on the selected colors
    const colorsSku = selectedColors
      .map((color) => color.id)
      .sort()
      .join("-");
    
    const colorDisplay = selectedColors
      .map((color) => color.name)
      .join(", ");
    
    const finalName = "The Ryldo" + (colorsSku ? ` (${colorDisplay})` : "");
    const actualStripeProductId = stripeProduct ? stripeProduct.id : `ryldo-custom-${colorsSku}`; // Use correct 'id' property
    const cartItemId = `${actualStripeProductId}::${colorsSku || 'default'}`;

    // Structure the cart item correctly for the backend
    const newItem = {
      id: actualStripeProductId, // Use the Stripe Product ID or a consistent identifier
      cartItemId, // unique per color selection
      type: 'ryldo', // Identifier for the custom product
      name: finalName,
      price: productPrice, // Use the calculated price
      quantity: 1,
      image: productImage, // Use the selected image
      selectedColors: selectedColors.map(color => ({ // Pass the selected color objects
        id: color.id,
        name: color.name
      })),
       // Keep original stripeInfo for reference if needed
      _stripeInfoRef: stripeProduct ? { 
          stripeId: stripeProduct.id,
          priceId: stripeProduct.defaultPriceId, // Note: This might be the *default* price ID
          metadata: stripeProduct.metadata
      } : null,
    };

    setCartItems([...cartItems, newItem]);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);

    // Clear selected colors after adding to cart
    setSelectedColors([]);
    // Reset product image to default
    setProductImage("/images/products/ryldo/placeholder.jpg");
    // Reset price to base price
    setProductPrice(69.99);
    // Clear any validation errors
    setColorValidationError(false);
  };

  return (
    <div className="bg-blue-50 dark:bg-gray-900">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="text-center">
          <div className="flex justify-center mb-2">
            <img
              src="/ryscircus-logo.svg"
              alt="Ry's Circus Logo"
              className="h-40 w-auto"
            />
          </div>
          <h1 className="text-4xl tracking-tight font-extrabold text-blue-900 dark:text-blue-100 sm:text-5xl md:text-6xl font-handwritten">
            Ry&apos;s Circus
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-blue-600 dark:text-blue-400 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl font-handwritten">
            Home of the Ryldo and other exclusive merch
          </p>
        </div>
      </div>

      {/* The Ryldo */}
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
        id="featured"
      >
        <Card className="max-w-2xl mx-auto bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardHeader>
            <CardTitle className="text-gray-900 dark:text-white font-handwritten">
              The Ryldo
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-300 font-handwritten text-lg">
              An exact replica handmade by me with the highest quality
              materials.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 product-gallery-container h-[400px]">
              {isGalleryLoading ? (
                <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded-lg h-full w-full"></div>
              ) : galleryImages.length > 0 ? (
                <div className="relative h-full">
                  <ImageGallery
                    items={galleryImages}
                    showPlayButton={selectedColors.length === 0}
                    showFullscreenButton={false}
                    showNav={true}
                    thumbnailPosition="bottom"
                    lazyLoad={true}
                    showThumbnails={false}
                    additionalClass="product-gallery-custom h-full"
                    autoPlay={selectedColors.length === 0}
                    slideInterval={10000}
                    renderItem={(item) => (
                      <div className="image-gallery-image h-full">
                        <img
                          src={item.original}
                          alt={item.originalAlt}
                          className="w-full h-full object-contain cursor-default"
                          style={{ pointerEvents: "none" }}
                        />
                      </div>
                    )}
                  />
                  {selectedColors.length > 0 && (
                    <div className="absolute top-2 right-2 z-10">
                      <Badge
                        variant="secondary"
                        className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                      >
                        {selectedColors.map((c) => c.name).join(", ")}
                      </Badge>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-full relative overflow-hidden">
                  <img
                    src="/images/products/ryldo/placeholder.jpg"
                    alt="The Ryldo"
                    className="rounded-lg object-contain w-full h-full shadow-md border border-gray-100 dark:border-gray-700"
                    style={{ pointerEvents: "none" }}
                  />
                </div>
              )}
            </div>
            <div className="space-y-4">
              <p className="text-lg text-gray-700 dark:text-gray-300 font-handwritten">
                Want to hold a piece of Ryan in your hands? Now you can! Become
                a true fan and show your support by grabbing one now.
              </p>

              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 font-handwritten">
                    Select Colors
                  </h3>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center text-gray-500 dark:text-gray-400 cursor-help">
                          <Info className="h-4 w-4 mr-1" />
                          <span className="text-xs font-handwritten">Color Help</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        <p className="font-handwritten">
                          Select up to 3 colors. Each additional color adds
                          $10.00 to the base price.
                        </p>
                        <p className="mt-1 font-handwritten">
                          Skin tone can only be selected as a single color.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {isLoading ? (
                  <div className="py-4 text-center text-gray-500 dark:text-gray-400 font-handwritten">
                    Loading available colors...
                  </div>
                ) : error ? (
                  <div className="py-4 text-center text-red-500 font-handwritten">{error}</div>
                ) : (
                  <>
                    {/* Skin Tones Row */}
                    {availableColors.some((color) => color.type === "skin") && (
                      <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-handwritten">
                          Skin Tones
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 italic font-handwritten">
                          Hover over colors or names to see enlarged views
                        </p>
                        <div className="grid grid-cols-5 gap-3">
                          {availableColors
                            .filter((color) => color.type === "skin")
                            .map((color, index) => (
                              <div
                                key={color.id || `skin-${index}`}
                                className="flex flex-col items-center"
                              >
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="flex flex-col items-center">
                                        <button
                                          className={`w-10 h-10 rounded-full border-2 p-0.5 mb-1 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer ${
                                            selectedColors.some(
                                              (c) => c.id === color.id,
                                            )
                                              ? "border-blue-500 scale-110 shadow-md"
                                              : "border-gray-300 dark:border-gray-600 hover:border-blue-300"
                                          }`}
                                          onClick={() =>
                                            handleColorSelect(color.id)
                                          }
                                        >
                                          <img
                                            src={
                                              color.image ||
                                              "/images/products/ryldo/placeholder.jpg"
                                            }
                                            alt={color.name}
                                            className="w-full h-full rounded-full object-cover"
                                          />
                                        </button>
                                        <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-full font-handwritten">
                                          {color.name.length > 10
                                            ? `${color.name.substring(0, 8)}...`
                                            : color.name}
                                        </span>
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent
                                      side="bottom"
                                      className="text-xs p-2"
                                    >
                                      <div className="flex flex-col items-center space-y-2">
                                        <div className="w-16 h-16 rounded-full border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
                                          <img
                                            src={
                                              color.image ||
                                              "/images/products/ryldo/placeholder.jpg"
                                            }
                                            alt={color.name}
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                        <span className="font-handwritten">{`${color.name} (Skin Tone - can only be selected alone)`}</span>
                                      </div>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Divider */}
                    {availableColors.some((color) => color.type === "skin") && (
                      <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>
                    )}

                    {/* Regular Colors Grid */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 font-handwritten">
                        Regular Colors
                      </h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 italic font-handwritten">
                        Hover over colors or names to see enlarged views
                      </p>
                      <div className="grid grid-cols-5 gap-3">
                        {availableColors
                          .filter((color) => color.type !== "skin")
                          .map((color, index) => (
                            <div
                              key={color.id || `color-${index}`}
                              className="flex flex-col items-center"
                            >
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex flex-col items-center">
                                      <button
                                        className={`w-10 h-10 rounded-full border-2 p-0.5 mb-1 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all cursor-pointer ${
                                          selectedColors.some(
                                            (c) => c.id === color.id,
                                          )
                                            ? "border-blue-500 scale-110 shadow-md"
                                            : "border-gray-300 dark:border-gray-600 hover:border-blue-300"
                                        }`}
                                        onClick={() =>
                                          handleColorSelect(color.id)
                                        }
                                        disabled={
                                          selectedColors.length >= 3 &&
                                          !selectedColors.some(
                                            (c) => c.id === color.id,
                                          )
                                        }
                                      >
                                        <img
                                          src={
                                            color.image ||
                                            "/images/products/ryldo/placeholder.jpg"
                                          }
                                          alt={color.name}
                                          className="w-full h-full rounded-full object-cover"
                                        />
                                      </button>
                                      <span className="text-xs text-gray-600 dark:text-gray-400 truncate max-w-full font-handwritten">
                                        {color.name.length > 10
                                          ? `${color.name.substring(0, 8)}...`
                                          : color.name}
                                      </span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent
                                    side="bottom"
                                    className="text-xs p-2"
                                  >
                                    <div className="flex flex-col items-center space-y-2">
                                      <div className="w-16 h-16 rounded-full border-2 border-gray-200 dark:border-gray-700 overflow-hidden">
                                        <img
                                          src={
                                            color.image ||
                                            "/images/products/ryldo/placeholder.jpg"
                                          }
                                          alt={color.name}
                                          className="w-full h-full object-cover"
                                        />
                                      </div>
                                      <span className="font-handwritten">{color.name}</span>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          ))}
                      </div>
                    </div>
                  </>
                )}

                {/* New component to display selected colors */}
                <SelectedColorsDisplay
                  selectedColors={selectedColors}
                  onRemove={handleColorSelect}
                />
              </div>

              <div className="flex flex-col space-y-4 md:space-y-6">
                {isStripeLoading ? (
                  <div className="text-blue-600 dark:text-blue-400 flex items-center">
                    <span className="mr-2">Loading price...</span>
                    <div className="animate-spin h-4 w-4 border-2 border-blue-600 dark:border-blue-400 rounded-full border-t-transparent"></div>
                  </div>
                ) : (
                  <p className="text-lg font-medium text-blue-600 dark:text-blue-400">
                    ${productPrice.toFixed(2)}
                    {stripeProduct && (
                      <span className="ml-2 text-sm text-green-600 dark:text-green-400">
                        via Stripe
                      </span>
                    )}
                    {selectedColors.length > 1 && (
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        (Base + ${((selectedColors.length - 1) * 10).toFixed(2)} for
                        additional colors)
                      </span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            {colorValidationError && (
              <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                <AlertDescription className="text-red-600 dark:text-red-400 font-handwritten">
                  Please select at least one color before adding to cart
                </AlertDescription>
              </Alert>
            )}
            <Button
              id="add-to-cart-button"
              className={`w-full text-white font-handwritten ${selectedColors.length === 0 
                ? 'bg-gray-400 hover:bg-gray-500 dark:bg-gray-600 dark:hover:bg-gray-700' 
                : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'}`}
              onClick={handleAddToCart}
            >
              {selectedColors.length === 0 ? 'Select a Color First' : 'Add to Cart'}
            </Button>
          </CardFooter>
        </Card>
      </div>

      {/* How It's Made Section */}
      <div className="bg-blue-50 dark:bg-gray-900 py-16" id="how-its-made">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-extrabold text-blue-900 dark:text-blue-100 font-handwritten relative inline-block">
              Behind the Madness: How It&apos;s Made
              <div className="absolute -z-10 w-full h-full bg-yellow-100 dark:bg-blue-900/30 -rotate-1 rounded-lg -left-2 -top-2"></div>
            </h2>
          </div>
          
          {/* First sentence - big and stylized */}
          <div className="text-center mb-12">
            <p className="text-2xl italic font-medium text-blue-800 dark:text-blue-200 max-w-3xl mx-auto">
              Behind all the jokes, explosions, and questionable wisdom is actual craftsmanship—seriously.
            </p>
          </div>
          
          {/* Visual Section: The Tools of Chaos */}
          <div className="mb-16">
            <h3 className="text-2xl font-handwritten text-blue-800 dark:text-blue-300 mb-6">The Tools of Chaos</h3>
            <div className="relative bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md">
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg mb-6 overflow-hidden relative">
                {/* Workspace image */}
                <img 
                  src="/images/how-its-made/tools-of-chaos.png" 
                  alt="The Tools of Chaos - Silicone crafting workspace with Smooth-On containers, pigments, molds and tools" 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/images/placeholder.png';
                  }}
                />
                
                {/* Image labels */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2">
                  <p className="text-sm font-handwritten">The Workspace: Where Magic (and Occasional Mistakes) Happen</p>
                </div>
                
                {/* Overlay labels */}
                <div className="absolute bottom-[10%] left-1/2 transform -translate-x-1/2 translate-y-0">
                  <div className="h-12 w-px bg-blue-500 dark:bg-blue-400 mb-2"></div>
                  <div className="bg-yellow-100 dark:bg-blue-900 p-2 rounded-lg shadow rotate-3">
                    <p className="font-handwritten text-sm text-blue-800 dark:text-blue-200">Precision instruments /<br/>probably overkill</p>
                  </div>
                </div>
                
                <div className="absolute bottom-1/3 left-1/4 transform -translate-x-1/2 translate-y-1/2">
                  <div className="bg-yellow-100 dark:bg-blue-900 p-2 rounded-lg shadow -rotate-2">
                    <p className="font-handwritten text-sm text-blue-800 dark:text-blue-200">Where the magic starts<br/>(also coffee stains live here)</p>
                  </div>
                  <div className="h-12 w-px bg-blue-500 dark:bg-blue-400 -mr-4"></div>
                </div>
                
                <div className="absolute top-[20%] right-[20%] transform translate-x-1/2 -translate-y-1/2">
                  <div className="h-12 w-px bg-blue-500 dark:bg-blue-400 -mr-4"></div>
                  <div className="bg-yellow-100 dark:bg-blue-900 p-2 rounded-lg shadow rotate-6">
                    <p className="font-handwritten text-sm text-blue-800 dark:text-blue-200">Chaos corner /<br/>don&apos;t touch this</p>
                  </div>
                </div>
              </div>

              <p className="text-center text-sm italic text-gray-500 dark:text-gray-400">
                This may or may not be real. No, I&apos;m not licensed.
              </p>
            </div>
          </div>
          
          {/* Process Breakdown Section */}
          <div className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* CSS for equal height cards */}
              <style dangerouslySetInnerHTML={{ __html: `
                .process-card {
                  display: flex;
                  flex-direction: column;
                  height: 100%;
                }
                .process-card-body {
                  flex-grow: 1;
                }
              `}} />
              {/* Step 1: Materials */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md transform hover:scale-105 transition-transform process-card">
                <div className="flex justify-center mb-4">
                  <div className="w-full h-48 overflow-hidden rounded-lg mb-2">
                    <img 
                      src="/images/how-its-made/materials.png" 
                      alt="Silicone materials - blue and yellow containers with colorful pigment jars" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.png';
                      }}
                    />
                  </div>
                </div>
                <div className="process-card-body">
                  <h4 className="text-xl font-handwritten text-blue-800 dark:text-blue-300 mb-2 text-center">Materials</h4>
                  <p className="text-gray-700 dark:text-gray-300 font-handwritten">
                    Only the good stuff here! High-quality silicone, made in the USA, that&apos;s safe for your skin and built to last through whatever chaos you decide to throw at it.
                  </p>
                </div>

              </div>
              
              {/* Step 2: Mixing */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md transform hover:scale-105 transition-transform process-card">
                <div className="flex justify-center mb-4">
                  <div className="w-full h-48 overflow-hidden rounded-lg mb-2">
                    <img 
                      src="/images/how-its-made/mixing.png" 
                      alt="Silicone mixing with blue and yellow silicone being poured into a bucket with colorful pigments" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.png';
                      }}
                    />
                  </div>
                </div>
                <div className="process-card-body">
                  <h4 className="text-xl font-handwritten text-blue-800 dark:text-blue-300 mb-2 text-center">Mixing</h4>
                  <p className="text-gray-700 dark:text-gray-300 font-handwritten">
                    We use Smooth-On silicone and mix in mica pigments to create colors that would make a unicorn jealous. Each batch is hand-mixed with the precision of a mad scientist.
                  </p>
                </div>

              </div>
              
              {/* Step 3: Vacuum Degassing */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md transform hover:scale-105 transition-transform process-card">
                <div className="flex justify-center mb-4">
                  <div className="w-full h-48 overflow-hidden rounded-lg mb-2">
                    <img 
                      src="/images/how-its-made/vacuum-degassing.png" 
                      alt="Vacuum chamber with pressure gauge and pump for removing air bubbles" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.png';
                      }}
                    />
                  </div>
                </div>
                <div className="process-card-body">
                  <h4 className="text-xl font-handwritten text-blue-800 dark:text-blue-300 mb-2 text-center">Vacuum Degassing</h4>
                  <p className="text-gray-700 dark:text-gray-300 font-handwritten">
                    We suck out all the air bubbles with our vacuum chamber, because nobody wants a bubbly toy (unless it&apos;s intentionally bubbly, which is a whole different thing).
                  </p>
                  <div className="mt-4 flex justify-center">
                    <p className="text-xs italic text-gray-500 dark:text-gray-400">Removes bubbles and doubt.</p>
                  </div>
                </div>
              </div>
              
              {/* Step 4: Made to Order */}
              <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-md transform hover:scale-105 transition-transform process-card">
                <div className="flex justify-center mb-4">
                  <div className="w-full h-48 overflow-hidden rounded-lg mb-2">
                    <img 
                      src="/images/how-its-made/made-to-order.png" 
                      alt="Customer consultation with craftsperson taking custom order notes" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder.png';
                      }}
                    />
                  </div>
                </div>
                <div className="process-card-body">
                  <h4 className="text-xl font-handwritten text-blue-800 dark:text-blue-300 mb-2 text-center">Made to Order</h4>
                  <p className="text-gray-700 dark:text-gray-300 font-handwritten">
                    Everything&apos;s made custom to your specs—no mass production, no shortcuts, no weird factory smell. Just handcrafted quality with a sprinkle of circus-grade weirdness.
                  </p>
                </div>

              </div>
            </div>
          </div>
          
          {/* Final Statement Block */}
          <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-md max-w-2xl mx-auto">
            <h3 className="text-2xl font-handwritten text-blue-800 dark:text-blue-300 mb-6 text-center">The Ry&apos;s Circus Promise</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start space-x-2">
                <div className="text-green-500 flex-shrink-0 mt-1">✅</div>
                <p className="text-gray-700 dark:text-gray-300 font-handwritten">Handcrafted with questionable music playing</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="text-green-500 flex-shrink-0 mt-1">✅</div>
                <p className="text-gray-700 dark:text-gray-300 font-handwritten">Custom-made to your exact specifications</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="text-red-500 flex-shrink-0 mt-1">❌</div>
                <p className="text-gray-700 dark:text-gray-300 font-handwritten">Mass-produced factory funk</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="text-green-500 flex-shrink-0 mt-1">✅</div>
                <p className="text-gray-700 dark:text-gray-300 font-handwritten">Sprinkle of circus-grade weirdness</p>
              </div>
            </div>
            
            {/* Ry's Circus Promise Image */}
            <div className="mt-6 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 aspect-video relative">
              <img 
                src="/images/how-its-made/rys-circus-promise.png" 
                alt="Ry's Circus Promise - A raccoon mixing colorful silicone with a banana character on a unicycle in a circus setting" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/images/placeholder.png';
                }}
              />
            </div>
          </div>
        </div>
      </div>



      {/* Notification */}
      {showNotification && (
        <div className="fixed bottom-4 right-4">
          <Alert className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <AlertTitle className="text-gray-900 dark:text-white">
              Success!
            </AlertTitle>
            <AlertDescription className="text-gray-600 dark:text-gray-300">
              Item added to cart
            </AlertDescription>
          </Alert>
        </div>
      )}
    </div>
  );
};

ProductLanding.propTypes = {
  cartItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number.isRequired,
      quantity: PropTypes.number.isRequired,
    }),
  ).isRequired,
  setCartItems: PropTypes.func.isRequired,
};

export default ProductLanding;
/* eslint-enable react/prop-types */
